/**
 * Synthesised sound effects, no audio files. Browsers block audio until a user gesture,
 * so `unlock()` is called from the first tap. Same bones as lu-or-labu's Sfx.
 */
/** sample files under public/sfx/, played instead of the synth when they exist (see docs/SOUNDS.md) */
const SAMPLES: Record<string, string> = {
  splash: '/sfx/splash.m4a',
  splashFar: '/sfx/splash-far.m4a',
  ocean: '/sfx/ocean.m4a',
  ocean2: '/sfx/ocean2.m4a',
  ocean3: '/sfx/ocean3.m4a',
  feedMusic: '/music/feed.m4a',
};
/** how loud the Feed round's tune is */
const MUSIC_GAIN = 0.32;
export const SEAS = ['ocean', 'ocean2', 'ocean3'] as const;
/** which sea plays: all three are CC-licensed, so a round gets one at random; `coco.ocean` (1–3) pins one for the debug key */
let seaThisRound: (typeof SEAS)[number] = SEAS[Math.floor(Math.random() * SEAS.length)];
export function oceanChoice(): (typeof SEAS)[number] {
  try {
    const pinned = localStorage.getItem('coco.ocean');
    if (pinned) return SEAS[Math.min(SEAS.length, Math.max(1, Number(pinned))) - 1];
  } catch {
    /* no storage */
  }
  return seaThisRound;
}
/** pick a different sea for the next round */
export function rollSea(): void {
  seaThisRound = SEAS[Math.floor(Math.random() * SEAS.length)];
}
/** how loud the sea is under everything else */
const AMBIENT_GAIN = 0.22;

export class Sfx {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private samples = new Map<string, AudioBuffer>();
  private loading = false;
  private ambientSrc: AudioBufferSourceNode | null = null;
  private ambientGain: GainNode | null = null;
  /** the game wants the sea right now (a round is on and not paused) */
  private ambientWanted = false;
  /** the Feed round's tune, when it is wanted instead of the sea */
  private musicWanted = false;
  private musicSrc: AudioBufferSourceNode | null = null;
  private musicGain: GainNode | null = null;
  /** recorded lines, decoded on demand and cached by name */
  private voices = new Map<string, AudioBuffer | null>();
  private speaking: AudioBufferSourceNode | null = null;
  enabled = true;

  unlock(): void {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') void this.ctx.resume();
      return;
    }
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    this.ctx = new Ctor();
    this.master = this.ctx.createGain();
    this.master.gain.value = 0.5;
    this.master.connect(this.ctx.destination);
    void this.loadSamples();
  }

  /** fetch and decode the sample files once; a missing file just means the synth is used */
  private async loadSamples(): Promise<void> {
    if (this.loading || !this.ctx) return;
    this.loading = true;
    for (const [name, url] of Object.entries(SAMPLES)) {
      try {
        const r = await fetch(url);
        if (!r.ok) continue;
        const buf = await this.ctx.decodeAudioData(await r.arrayBuffer());
        this.samples.set(name, buf);
        if (name === oceanChoice() && this.ambientWanted) this.ambient(true);
        if (name === 'feedMusic' && this.musicWanted) this.music(true);
      } catch {
        /* no sample: synth fallback */
      }
    }
  }

  /** fetch and decode a recorded line from public/voice/<name>.m4a; null if there is no such file */
  private async voice(name: string): Promise<AudioBuffer | null> {
    if (this.voices.has(name)) return this.voices.get(name)!;
    if (!this.ctx) return null;
    this.voices.set(name, null); // one fetch per name
    try {
      const r = await fetch(`/voice/${name}.m4a`);
      if (!r.ok) return null;
      const buf = await this.ctx.decodeAudioData(await r.arrayBuffer());
      this.voices.set(name, buf);
      return buf;
    } catch {
      return null;
    }
  }

  /** warm the cache for a set of lines (after the first tap, in the background) */
  preloadVoices(names: string[]): void {
    for (const n of names) void this.voice(n);
  }

  /**
   * Play a recorded line if it exists. A new line cuts the previous one and the sea ducks while it
   * plays. Resolves with the clip's length in ms (0 if there was nothing to play) so a pop-up can
   * stay up long enough.
   */
  async say(name: string): Promise<number> {
    const buf = await this.voice(name);
    if (!buf || !this.enabled || !this.ctx || !this.master) return 0;
    this.speaking?.stop();
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const g = this.ctx.createGain();
    g.gain.value = 1;
    src.connect(g).connect(this.master);
    const t0 = this.ctx.currentTime;
    if (this.ambientGain) {
      this.ambientGain.gain.cancelScheduledValues(t0);
      this.ambientGain.gain.setTargetAtTime(AMBIENT_GAIN * 0.35, t0, 0.05);
      this.ambientGain.gain.setTargetAtTime(this.ambientWanted ? AMBIENT_GAIN : 0.0001, t0 + buf.duration, 0.3);
    }
    if (this.musicGain) {
      this.musicGain.gain.cancelScheduledValues(t0);
      this.musicGain.gain.setTargetAtTime(MUSIC_GAIN * 0.3, t0, 0.05);
      this.musicGain.gain.setTargetAtTime(this.musicWanted ? MUSIC_GAIN : 0.0001, t0 + buf.duration, 0.3);
    }
    src.start();
    this.speaking = src;
    src.onended = () => {
      if (this.speaking === src) this.speaking = null;
    };
    return Math.round(buf.duration * 1000);
  }

  /** the Feed round's chase tune, looping; same rules as the sea */
  music(on: boolean): void {
    this.musicWanted = on;
    if (!this.ctx || !this.master) return;
    if (!on || !this.enabled) {
      if (this.musicGain) this.musicGain.gain.linearRampToValueAtTime(0.0001, this.ctx.currentTime + 0.4);
      const src = this.musicSrc;
      this.musicSrc = null;
      if (src) setTimeout(() => src.stop(), 500);
      return;
    }
    if (this.musicSrc) return;
    const buf = this.samples.get('feedMusic');
    if (!buf) return;
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, this.ctx.currentTime);
    g.gain.linearRampToValueAtTime(MUSIC_GAIN, this.ctx.currentTime + 1);
    src.connect(g).connect(this.master);
    src.start();
    this.musicSrc = src;
    this.musicGain = g;
  }

  /** the sea, looping softly under everything; off when sound is off or the app is hidden */
  ambient(on: boolean): void {
    this.ambientWanted = on;
    if (!this.ctx || !this.master) return;
    if (!on || !this.enabled) {
      if (this.ambientGain) this.ambientGain.gain.linearRampToValueAtTime(0.0001, this.ctx.currentTime + 0.4);
      const src = this.ambientSrc;
      this.ambientSrc = null;
      if (src) setTimeout(() => src.stop(), 500);
      return;
    }
    if (this.ambientSrc) return;
    const buf = this.samples.get(oceanChoice());
    if (!buf) return;
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.0001, this.ctx.currentTime);
    g.gain.linearRampToValueAtTime(AMBIENT_GAIN, this.ctx.currentTime + 2);
    src.connect(g).connect(this.master);
    src.start();
    this.ambientSrc = src;
    this.ambientGain = g;
  }

  /** play a decoded sample; false if it isn't loaded. A little random pitch keeps repeats from sounding canned. */
  private sample(name: string, gain = 1, rateJitter = 0.08): boolean {
    const buf = this.samples.get(name);
    if (!this.enabled || !this.ctx || !this.master || !buf) return false;
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = 1 + (Math.random() * 2 - 1) * rateJitter;
    const g = this.ctx.createGain();
    g.gain.value = gain;
    src.connect(g).connect(this.master);
    src.start();
    return true;
  }

  private tone(freq: number, dur: number, type: OscillatorType = 'sine', gain = 0.6, slideTo?: number, at = 0, attack = 0.008): void {
    if (!this.enabled || !this.ctx || !this.master) return;
    const t0 = this.ctx.currentTime + at;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = type;
    o.frequency.setValueAtTime(freq, t0);
    if (slideTo) o.frequency.exponentialRampToValueAtTime(slideTo, t0 + dur);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(gain, t0 + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    o.connect(g).connect(this.master);
    o.start(t0);
    o.stop(t0 + dur + 0.02);
  }

  /**
   * A burst of filtered noise. `attack` seconds of fade-in keeps it from clicking; `lowpassTo`
   * sweeps the filter down over the burst, which is what makes water sound wet rather than like a hit.
   */
  private noise(dur: number, gain = 0.5, lowpass = 1200, at = 0, highpass = 0, attack = 0.004, lowpassTo?: number, q = 0.7): void {
    if (!this.enabled || !this.ctx || !this.master) return;
    const ctx = this.ctx;
    const buf = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * dur), ctx.sampleRate);
    const d = buf.getChannelData(0);
    const rise = Math.max(1, Math.floor(ctx.sampleRate * attack));
    for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / d.length) ** 2 * Math.min(1, i / rise);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const t0 = ctx.currentTime + at;
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.Q.value = q;
    f.frequency.setValueAtTime(lowpass, t0);
    if (lowpassTo) f.frequency.exponentialRampToValueAtTime(lowpassTo, t0 + dur);
    const g = ctx.createGain();
    g.gain.value = gain;
    let chain: AudioNode = src.connect(f);
    if (highpass > 0) {
      const hp = ctx.createBiquadFilter();
      hp.type = 'highpass';
      hp.Q.value = q;
      hp.frequency.value = highpass;
      chain = chain.connect(hp);
    }
    chain.connect(g).connect(this.master);
    src.start(t0);
  }

  /** something leaves the hand: a soft pop, not a whip */
  throwRock(): void {
    this.tone(150, 0.08, 'sine', 0.4, undefined, 0, 0.004);
    this.noise(0.07, 0.1, 700, 0, 0, 0.02);
  }
  /** picking something else from the bucket */
  click(): void {
    this.tone(900, 0.03, 'square', 0.12);
    this.tone(600, 0.04, 'square', 0.1, undefined, 0.05);
  }
  /** a shark has turned toward the boat: two low notes, you know the ones */
  jaws(): void {
    this.tone(82, 0.32, 'sawtooth', 0.35);
    this.tone(41, 0.32, 'sine', 0.4);
    this.tone(87, 0.42, 'sawtooth', 0.35, undefined, 0.42);
    this.tone(43.5, 0.42, 'sine', 0.4, undefined, 0.42);
  }
  /** it reached the hull */
  chomp(): void {
    this.noise(0.12, 0.7, 700);
    this.tone(70, 0.3, 'sine', 0.7, 40);
    this.noise(0.4, 0.4, 2400, 0.08, 200);
  }
  /** a coconut takes a hit: a hollow wooden knock, two taps */
  crack(): void {
    // the knock: a damped low body tone and a sharper tap, with a short click of noise
    this.tone(190, 0.12, 'sine', 0.7, 120);
    this.tone(430, 0.07, 'triangle', 0.35, 300);
    this.noise(0.03, 0.5, 5000, 0, 1200);
    this.tone(175, 0.1, 'sine', 0.45, 110, 0.11);
    this.noise(0.025, 0.3, 4500, 0.11, 1200);
  }
  /**
   * Something hits only water. Layers, all noise and soft sines, no pitched sweeps (those ring
   * like sheet metal): the slap of entry, the body of the splash whose brightness falls as it
   * decays, a low plunk with a slow attack, and a bubble or two surfacing after.
   */
  splash(far = false): void {
    if (this.sample(far && this.samples.has('splashFar') ? 'splashFar' : 'splash', far ? 0.45 : 1)) return;
    const g = far ? 0.4 : 1;
    // the slap: short, bright, soft-edged
    this.noise(0.07, 0.45 * g, 5000, 0, 400, 0.012, 1500);
    // the body: brightness sweeping down as it decays
    this.noise(far ? 0.35 : 0.6, 0.55 * g, 2600, 0.02, 120, 0.05, far ? 500 : 350);
    // the plunk: a low sine with a gentle attack, no slide
    this.tone(110, 0.18, 'sine', 0.28 * g, undefined, 0.01, 0.03);
    // the bubbles
    if (!far) {
      this.tone(700, 0.04, 'sine', 0.07, 1100, 0.2, 0.01);
      this.tone(560, 0.04, 'sine', 0.06, 900, 0.31, 0.01);
    }
  }
  /** the last seconds: a soft tick */
  tick(): void {
    this.tone(1200, 0.05, 'sine', 0.18, 900, 0, 0.004);
  }
  /** a capuchin chattering: quick rising chirps */
  chatter(): void {
    for (let i = 0; i < 5; i++) this.tone(900 + i * 120, 0.06, 'square', 0.1, 1500 + i * 100, i * 0.09, 0.004);
  }
  /** a turtle takes the fish */
  munch(): void {
    this.noise(0.05, 0.35, 1500, 0, 300);
    this.tone(300, 0.08, 'triangle', 0.25, 220, 0.03);
    this.tone(420, 0.14, 'sine', 0.3, 560, 0.1);
  }
  /** you scared a friend */
  strike(): void {
    this.tone(120, 0.35, 'sawtooth', 0.35, 70);
    this.tone(126, 0.35, 'square', 0.14, 74);
    this.noise(0.3, 0.3, 900, 0.05);
  }
  /** the round is over */
  win(): void {
    const notes = [523, 659, 784, 1047];
    notes.forEach((f, i) => this.tone(f, 0.18, 'triangle', 0.4, undefined, i * 0.12));
    this.tone(1319, 0.5, 'triangle', 0.35, undefined, notes.length * 0.12);
  }
}
